import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Plus, RefreshCcw, ChevronRight, Wallet, Save, X, Trash2, PenLine, 
  CreditCard, CalendarClock, CheckCircle2 
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Condiciones de Pago - Enterprise
 * Refactorizado al estilo Productos / Unidades
 * Estructura: KPIs + Formulario Inline (Izq) + Listado (Der)
 */
function CondicionesPago() {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // form states
  const [editingId, setEditingId] = useState(null);
  // const [formOpen, setFormOpen] = useState(false); // COMENTADO: Ya no se usa toggle, el form siempre está visible a la izquierda
  const [name, setName] = useState('');
  const [days, setDays] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile?.organization_id) fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  async function fetchItems() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('payment_conditions')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .order('days_until_due', { ascending: true });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar condiciones');
    } finally {
      setLoading(false);
    }
  }

  async function guardar(e) {
    e.preventDefault();
    if (!name.trim()) return toast.error('El nombre es obligatorio');

    setSaving(true);
    try {
      const payload = {
        name: name.trim().toUpperCase(), // Estandarización a mayúsculas
        days_until_due: parseInt(days) || 0,
        organization_id: profile.organization_id,
        is_active: true
      };

      if (editingId) {
        const { error } = await supabase
          .from('payment_conditions')
          .update(payload)
          .eq('id', editingId);
        if (error) throw error;
        toast.success('Condición actualizada');
      } else {
        const { error } = await supabase
          .from('payment_conditions')
          .insert([payload]);
        if (error) throw error;
        toast.success('Condición creada');
      }
      
      limpiarForm();
      fetchItems();
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar esta condición?')) return;
    try {
      const { error } = await supabase
        .from('payment_conditions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Eliminado correctamente');
      fetchItems();
    } catch (error) {
      console.error(error);
      toast.error('No se puede eliminar (en uso)');
    }
  }

  function editar(item) {
    setEditingId(item.id);
    setName(item.name);
    setDays(item.days_until_due);
    // setFormOpen(true); // COMENTADO: El form está visible
  }

  function limpiarForm() {
    setEditingId(null);
    setName('');
    setDays(0);
    // setFormOpen(false); // COMENTADO
  }

  // --- KPIS ---
  const kpis = useMemo(() => {
    const total = items.length;
    const contado = items.filter(i => i.days_until_due === 0).length;
    const financiado = items.filter(i => i.days_until_due > 0).length;
    return { total, contado, financiado };
  }, [items]);

  return (
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <CreditCard size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Condiciones de Venta</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Plazos y vencimientos para clientes.
            </p>
          </div>
        </div>
      </div>

      {/* KPI SECTION */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Opciones</p>
            <Wallet size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Disponibles</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Contado / Al Día</p>
            <CheckCircle2 size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.contado}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Vencimiento inmediato</p>
        </div>

        <div className="imp-kpi">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Financiación</p>
                <CalendarClock size={16} className="text-purple-500" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.financiado}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">A plazo</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* COLUMNA IZQUIERDA: FORMULARIO */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Plus size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                  {editingId ? 'Editar Condición' : 'Nueva Condición'}
                </span>
              </div>
              {editingId && (
                <button onClick={limpiarForm} className="text-[9px] font-bold uppercase text-white/60 hover:text-white flex items-center gap-1 transition-colors">
                    <X size={12}/> Cancelar
                </button>
              )}
            </div>

            <div className="p-4">
                <form onSubmit={guardar} className="space-y-3">
                    <div className="space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Nombre Descriptivo</label>
                        <input 
                            type="text"
                            placeholder="EJ: CONTADO, 30 DÍAS FF..."
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                            autoFocus
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Días hasta vencimiento</label>
                        <div className="relative">
                            <input 
                                type="number"
                                min="0"
                                placeholder="0"
                                value={days}
                                onChange={(e) => setDays(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-100 pl-3 pr-10 py-2.5 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-black text-slate-400 uppercase">Días</span>
                        </div>
                    </div>

                    <div className="pt-2">
                        <button 
                            type="submit" 
                            disabled={saving}
                            className={`w-full py-3.5 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 shadow-lg
                                ${editingId 
                                    ? 'bg-[#51A7FD] text-white hover:bg-blue-600' 
                                    : 'bg-[#3ff2aa] text-[#0B2A36] hover:scale-[1.02] active:scale-95 shadow-emerald-500/20'}`}
                        >
                            {saving ? <RefreshCcw className="animate-spin" size={14} /> : <Save size={16} />}
                            {editingId ? 'Actualizar' : 'Guardar Condición'}
                        </button>
                    </div>
                </form>
            </div>
          </div>
        </div>

        {/* COLUMNA DERECHA: LISTADO */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[400px]">
            <div className="overflow-x-auto imp-table-wrap">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    <th className="px-5 py-3 pl-5">Descripción</th>
                    <th className="px-4 py-3 text-center">Plazo</th>
                    <th className="px-4 py-3 text-right pr-5">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {loading ? (
                    <tr>
                      <td colSpan="3" className="py-12 text-center text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                        <RefreshCcw className="animate-spin mx-auto mb-2" size={20} />
                        Cargando...
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="py-16 text-center opacity-50 flex flex-col items-center">
                        <CalendarClock size={40} className="text-slate-300 mb-2" strokeWidth={1} />
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          No hay condiciones registradas
                        </p>
                      </td>
                    </tr>
                  ) : (
                    items.map((m) => (
                      <tr key={m.id} className="hover:bg-blue-50/20 transition-all group">
                        <td className="px-5 py-3 pl-5">
                          <span className="text-[11px] font-black text-slate-700 uppercase tracking-tight">{m.name}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {m.days_until_due === 0 ? (
                            <span className="inline-flex items-center px-2 py-1 rounded bg-emerald-50 border border-emerald-100 text-emerald-600 text-[9px] font-black uppercase">
                              Contado
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded bg-purple-50 border border-purple-100 text-purple-600 text-[9px] font-black uppercase">
                              {m.days_until_due} Días
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 pr-5 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => editar(m)}
                              className="p-1.5 text-slate-300 hover:text-[#51A7FD] hover:bg-blue-50 rounded-lg transition-all"
                              title="Editar"
                            >
                              <PenLine size={14} />
                            </button>
                            <button
                              onClick={() => eliminar(m.id)}
                              className="p-1.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
}

export default CondicionesPago;